import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { customBilling } from '@/api/customBilling';
import { useBranding } from '@/hooks/useBranding';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft, Printer } from 'lucide-react';
import {
  INVOICE_STATUS_STYLES, formatMoney, formatDateOnly, formatDateTime, formatQty,
} from './customInvoiceShared';
import { cn } from '@/lib/cn';

/**
 * Print-friendly invoice view — a clean A4 sheet carrying the reseller's
 * brand name, meant for the browser's print-to-PDF (no PDF dependency by
 * design; see AI-builder rule 22).
 *
 * Rendering strategy: the whole view is PORTALED to document.body so it can
 * escape the portal shell. On screen it's a full-viewport "document mode"
 * overlay (slate backdrop, centered sheet, toolbar). In print, a scoped
 * stylesheet hides the app root (#root) entirely and flattens the overlay
 * to static flow — the sheet becomes the only content in the document, so
 * multi-page invoices paginate naturally.
 */
export function CustomInvoicePrint() {
  const { id } = useParams<{ id: string }>();
  const brand = useBranding();

  const { data, isLoading, error } = useQuery({
    queryKey: ['customInvoice', id],
    queryFn: () => customBilling.getInvoice(id!),
    enabled: !!id,
  });

  const invoice = data?.invoice;
  const items = data?.items ?? [];
  const statusStyle = invoice ? (INVOICE_STATUS_STYLES[invoice.status] ?? INVOICE_STATUS_STYLES.draft) : null;

  const body = (
    <div className="invoice-print-overlay fixed inset-0 z-[100] overflow-y-auto bg-slate-200/95">
      {/* Print isolation — scoped to this view; removed on unmount. */}
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }
          body > #root { display: none !important; }
          .invoice-print-overlay {
            position: static !important;
            inset: auto !important;
            overflow: visible !important;
            background: transparent !important;
          }
          .invoice-sheet {
            margin: 0 !important;
            width: auto !important;
            min-height: 0 !important;
            box-shadow: none !important;
            border: none !important;
            padding: 0 !important;
          }
          .print-hidden { display: none !important; }
          .invoice-sheet, .invoice-sheet * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>

      {/* Toolbar — screen only */}
      <div className="print-hidden sticky top-0 z-10 border-b border-slate-300/70 bg-white/85 backdrop-blur px-4 py-2.5">
        <div className="mx-auto flex w-full max-w-[210mm] items-center justify-between">
          <Button asChild variant="ghost" size="sm">
            <Link to={`/billing/custom-invoices/${id}`}><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to invoice</Link>
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 hidden sm:inline">Use “Save as PDF” in the print dialog for a file.</span>
            <Button size="sm" onClick={() => window.print()} disabled={!invoice}>
              <Printer className="h-4 w-4 mr-1.5" /> Print
            </Button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : error || !invoice ? (
        <div role="alert" className="mx-auto max-w-md py-24 text-center text-sm text-red-600">
          {error instanceof Error ? error.message : 'Invoice not found.'}
        </div>
      ) : (
        <div className="invoice-sheet mx-auto my-8 w-[210mm] max-w-full min-h-[297mm] bg-white text-slate-900 shadow-xl border border-slate-300/60 p-[16mm] flex flex-col">
          {/* Masthead */}
          <header className="flex items-start justify-between gap-6">
            <div>
              <div className="font-serif text-3xl font-semibold tracking-tight text-slate-900">
                {brand.brand_name}
              </div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-slate-400">
                Statement of charges
              </div>
            </div>
            <div className="text-end">
              <div className="text-[26px] font-light uppercase tracking-[0.3em] text-slate-300 leading-none">
                Invoice
              </div>
              <div className="mt-2 font-mono text-sm font-semibold text-slate-700">{invoice.number}</div>
              {statusStyle && (
                <span className={cn(
                  'mt-1.5 inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider leading-none',
                  statusStyle.className,
                )}>
                  {statusStyle.label}
                </span>
              )}
            </div>
          </header>

          <div className="mt-6 border-t-2 border-slate-900" />

          {/* Parties + meta */}
          <section className="mt-6 grid grid-cols-2 gap-8">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Billed to</div>
              <div className="mt-1.5 text-base font-medium">{invoice.customer_name ?? 'Customer'}</div>
              {invoice.customer_number != null && (
                <div className="text-xs text-slate-500 font-mono">
                  Account #{String(invoice.customer_number).padStart(4, '0')}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 content-start text-sm">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Billing period</div>
                <div className="mt-0.5 whitespace-nowrap">
                  {formatDateOnly(invoice.period_start)} – {formatDateOnly(invoice.period_end)}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Issued</div>
                <div className="mt-0.5">{formatDateTime(invoice.issued_at)}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Due</div>
                <div className="mt-0.5">{formatDateTime(invoice.due_at)}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Paid</div>
                <div className="mt-0.5">
                  {invoice.paid_at
                    ? <span className="font-medium text-emerald-700">{formatDateTime(invoice.paid_at)}</span>
                    : '—'}
                </div>
              </div>
            </div>
          </section>

          {/* Line items */}
          <section className="mt-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-slate-900">
                  <th className="py-2 text-start text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Description</th>
                  <th className="py-2 text-end text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Qty</th>
                  <th className="py-2 text-end text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Unit price</th>
                  <th className="py-2 text-end text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-slate-400">No line items.</td>
                  </tr>
                ) : items.map((it) => (
                  <tr key={it.id} className="border-b border-slate-200">
                    <td className="py-2.5 pe-4">{it.description}</td>
                    <td className="py-2.5 text-end tabular-nums text-slate-600">{formatQty(it.quantity)}</td>
                    <td className="py-2.5 text-end tabular-nums text-slate-600 whitespace-nowrap">{formatMoney(it.unit_price_cents)}</td>
                    <td className="py-2.5 text-end tabular-nums font-medium whitespace-nowrap">{formatMoney(it.amount_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals ledger */}
            <div className="mt-4 flex justify-end break-inside-avoid">
              <div className="w-64 space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-500">
                  <span>Subtotal</span>
                  <span className="tabular-nums">{formatMoney(invoice.subtotal_cents)}</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>Tax</span>
                  <span className="tabular-nums">{formatMoney(invoice.tax_cents)}</span>
                </div>
                <div className="flex justify-between items-baseline border-t-2 border-slate-900 pt-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">Total due</span>
                  <span className="tabular-nums text-lg font-semibold">{formatMoney(invoice.total_cents)}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Notes */}
          {invoice.notes && (
            <section className="mt-8 break-inside-avoid">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Notes</div>
              <p className="mt-1.5 text-sm text-slate-600 whitespace-pre-wrap">{invoice.notes}</p>
            </section>
          )}

          {/* Footer */}
          <footer className="mt-auto pt-10">
            <div className="border-t border-slate-200 pt-3 flex items-center justify-between text-[10px] text-slate-400">
              <span>{brand.brand_name} · Invoice {invoice.number}</span>
              <span>Generated {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
            </div>
          </footer>
        </div>
      )}
    </div>
  );

  return createPortal(body, document.body);
}
